import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Link } from "@mui/material";
import InfoPage from "../../components/InfoPage";
import LegalPage from "../../components/LegalPage";

export function Privacy() {
  return <LegalPage docKey="privacy" />;
}

export function Terms() {
  return <LegalPage docKey="terms" />;
}

export function SubProcessors() {
  return <LegalPage docKey="subprocessors" />;
}

/** Published privacy standard (/privacystandaard): k>=10 with full suppression,
 *  what employers never see, no AI, EU hosting, no tracking, the anonymous
 *  barometer, and professional confidentiality. Factual commitments only —
 *  keep in sync with the code and compliance/01-gdpr/barometer-anonimiteit-annex.md. */
export function PrivacyStandard() {
  return <LegalPage docKey="privacystandaard" />;
}

export function Cookies() {
  const { t } = useTranslation();
  // Names the actual cookies/storage in use: CarefundedToken (session), Stripe
  // fraud-prevention cookies, and Daily's technical storage during video —
  // strictly-necessary framing, so no consent banner is needed.
  return <InfoPage title={t("cookies.title")} paras={[t("cookies.p1"), t("cookies.p2"), t("cookies.p3"), t("cookies.p4"), t("cookies.p5")]} />;
}

/** /over-ons — missie (terugbetaalde zorg bereikbaar maken), wat we wél en
 *  níét doen, en de vermelding van Praxie Group BV. Feitelijk, zonder
 *  superlatieven. */
export function About() {
  const { t } = useTranslation();
  return (
    <InfoPage title={t("about.title")} paras={[t("about.p1"), t("about.p2")]}>
      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("about.missionTitle")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("about.mission1")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("about.mission2")}</Typography>

      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("about.roleTitle")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("about.role1")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("about.role2")}</Typography>

      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("about.companyTitle")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("about.company1")}</Typography>
      <Typography sx={{ color: "text.secondary", lineHeight: 1.7 }}>
        {t("about.contactLine")} <Link href="mailto:info@carefunded.be">info@carefunded.be</Link>
      </Typography>
    </InfoPage>
  );
}

// N.B. De uitgebreide /terugbetaling-pagina staat in ../Reimbursement.tsx
// (tabel per ziekenfonds uit content/mutualiteiten.ts).

/** "Hoe matchen wij?" — truthful description of the ranking rules in
 *  services/matching.ts (VI.99 §6 WER + P2B-style transparency): hard filters,
 *  the fixed points system, and the explicit no-pay-for-ranking / no-AI note.
 *  Keep in sync with speakhere-api/src/services/matching.ts. */
export function HowWeMatch() {
  const { t } = useTranslation();
  const bullets = (keys: string[]) => (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
      {keys.map((k) => (
        <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
      ))}
    </Box>
  );
  return (
    <InfoPage title={t("howMatch.title")} paras={[t("howMatch.intro")]}>
      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("howMatch.hardTitle")}</Typography>
      <Typography sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.7 }}>{t("howMatch.hardIntro")}</Typography>
      {bullets(["howMatch.hard1", "howMatch.hard2", "howMatch.hard3", "howMatch.hard4", "howMatch.hard5"])}

      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("howMatch.scoreTitle")}</Typography>
      <Typography sx={{ mb: 1.5, color: "text.secondary", lineHeight: 1.7 }}>{t("howMatch.scoreIntro")}</Typography>
      {bullets(["howMatch.score1", "howMatch.score2", "howMatch.score3"])}
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("howMatch.scoreOutro")}</Typography>

      <Typography variant="h2" sx={{ fontSize: "1.4rem", mt: 3, mb: 1.5 }}>{t("howMatch.noPayTitle")}</Typography>
      <Typography sx={{ mb: 2, color: "text.secondary", lineHeight: 1.7 }}>{t("howMatch.noPay1")}</Typography>
      <Typography sx={{ mb: 3, color: "text.secondary", lineHeight: 1.7 }}>{t("howMatch.noPay2")}</Typography>

      <Typography sx={{ color: "text.secondary" }}>
        {t("howMatch.contact")}
      </Typography>
    </InfoPage>
  );
}

export function Contact() {
  const { t } = useTranslation();
  return (
    <InfoPage title={t("contact.title")} paras={[t("contact.intro")]}>
      <Typography sx={{ mb: 1 }}>
        {t("contact.emailLabel")}: <Link href="mailto:support@carefunded.be">support@carefunded.be</Link>
      </Typography>
      <Typography sx={{ color: "text.secondary" }}>{t("contact.replyNote")}</Typography>
    </InfoPage>
  );
}
